import { useLayoutEffect, useState } from 'react';

export type Theme = 'light' | 'dark';
const storageKey = 'c-lab-theme';
const mediaQuery = '(prefers-color-scheme: dark)';

function storedTheme(): Theme | null {
	try {
		const value = localStorage.getItem(storageKey);
		return value === 'light' || value === 'dark' ? value : null;
	} catch {
		return null;
	}
}

function applyTheme(theme: Theme) {
	document.documentElement.dataset.theme = theme;
	document
		.querySelector('meta[name="theme-color"]')
		?.setAttribute('content', theme === 'dark' ? '#2f343f' : '#dcdcdd');
}

export function initializeTheme() {
	applyTheme(storedTheme() ?? (window.matchMedia(mediaQuery).matches ? 'dark' : 'light'));
}

export function useTheme() {
	const [preference, setPreference] = useState(storedTheme);
	const [systemDark, setSystemDark] = useState(() => window.matchMedia(mediaQuery).matches);
	const theme: Theme = preference ?? (systemDark ? 'dark' : 'light');

	useLayoutEffect(() => {
		const media = window.matchMedia(mediaQuery);
		const update = () => setSystemDark(media.matches);
		update();
		media.addEventListener('change', update);
		return () => media.removeEventListener('change', update);
	}, []);

	useLayoutEffect(() => applyTheme(theme), [theme]);

	function toggleTheme() {
		const next = theme === 'dark' ? 'light' : 'dark';
		setPreference(next);
		try {
			localStorage.setItem(storageKey, next);
		} catch {
			// Theme switching still works when browser storage is unavailable.
		}
	}

	return { theme, toggleTheme };
}
